import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatInTimeZone } from 'date-fns-tz';
import './Support.css';
import ConfirmationModal from '../../components/support/ConfirmationModal';
import SuccessModal from '../../components/support/SuccessModal';
const API_URL = process.env.REACT_APP_API_URL;

const FileRow = ({ file, sourceFileId, type, t, onUpload, isUploading, onDelete, onChangeSource }) => {
    const fileInputRef = useRef(null);
    const sourceFileInputRef = useRef(null); // YENİ: Kaynak dosya "değiştir" butonu için ref

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if(selectedFile) {
            onUpload(sourceFileId, selectedFile);
        }
    };
    
    // YENİ: Kaynak dosyayı değiştirme işlemini tetikleyen handler
    const handleSourceFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            onChangeSource(file.id, selectedFile);
        }
    };

    if (type === 'source') {
        // GÜNCELLENDİ: Kaynak dosyalar için artık silme ve değiştirme butonları var
        return (
            <div className="file-cell">
                <input type="file" ref={sourceFileInputRef} style={{display: 'none'}} onChange={handleSourceFileChange} />
                <span className="file-display"><i className="fas fa-file-alt"></i> {file.original_filename}</span>
                <div className="file-cell-actions">
                    <a href={`${API_URL}/${file.filepath}`} download={file.original_filename} className="download-icon-btn" title={t('download')}><i className="fas fa-download"></i></a>
                    <button onClick={() => sourceFileInputRef.current.click()} className="change-icon-btn" title={t('change')}><i className="fas fa-sync-alt"></i></button>
                    <button onClick={() => onDelete(file.id)} className="delete-icon-btn" title={t('delete')}><i className="fas fa-trash-alt"></i></button>
                </div>
            </div>
        );
    }
    
    // Hedef dosya bölümü (değişiklik yok)
    return (
        <div className="file-cell">
            <input type="file" ref={fileInputRef} style={{display: 'none'}} onChange={handleFileChange} />
            {isUploading ? <i className="fas fa-spinner fa-spin"></i> : 
             file ? (
                <>
                    <span className="file-display uploaded"><i className="fas fa-file-check"></i>{file.original_filename}</span>
                    <div className="file-cell-actions">
                        <a href={`${API_URL}/${file.filepath}`} download={file.original_filename} className="download-icon-btn" title={t('download')}><i className="fas fa-download"></i></a>
                        <button onClick={() => fileInputRef.current.click()} className="change-icon-btn" title={t('change')}><i className="fas fa-sync-alt"></i></button>
						<button onClick={() => onDelete(file.id)} className="delete-icon-btn" title={t('delete')}><i className="fas fa-trash-alt"></i></button>
                    </div>
                </>
             ) : (
                <button onClick={() => fileInputRef.current.click()} className="upload-placeholder-btn"><i className="fas fa-upload"></i> {t('upload')}</button>
             )}
        </div>
    );
};


const SupportJobDetailPage = () => {
    const { jobId } = useParams();
    const { t, i18n } = useTranslation();
    const { token } = useAuth();
    const location = useLocation(); 
    
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadingState, setUploadingState] = useState({});
    const [fromTab, setFromTab] = useState(location.state?.activeTab || 'pending_approval');
    const jobStatuses = ['pending_approval', 'available', 'assigned', 'in_progress', 'in_review', 'completed', 'cancelled'];
	const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [statusToChange, setStatusToChange] = useState(null);
	const [isConfirmEmailOpen, setIsConfirmEmailOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isEditingDeadline, setIsEditingDeadline] = useState(false); 
    const [isDeleteInvoiceModalOpen, setIsDeleteInvoiceModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [fileToDeleteId, setFileToDeleteId] = useState(null);
    const [newDeadline, setNewDeadline] = useState('');
	const invoiceFileInputRef = useRef(null);

    // YENİ: Kaynak dosyası silme onayı için state'ler
    const [isDeleteSourceModalOpen, setIsDeleteSourceModalOpen] = useState(false);
    const [sourceFileToDeleteId, setSourceFileToDeleteId] = useState(null);
    // YENİ: Yeni kaynak dosyası eklemek için ref
    const addSourceFileInputRef = useRef(null);


    const [eligibleTranslators, setEligibleTranslators] = useState({ loading: false, error: null, data: [] });
    const [selectedTranslatorId, setSelectedTranslatorId] = useState('');
    const [translatorFee, setTranslatorFee] = useState('');
    const [languageUpdate, setLanguageUpdate] = useState({ sourceLang: '', targetLangs: [] });
    const [availableLanguages, setAvailableLanguages] = useState([]);
    
    const fetchJobDetails = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            setLoading(true); setError('');
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/details`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_fetch_job_details')); }
            const data = await response.json();
            setJob(data);
            setLanguageUpdate({ sourceLang: data.source_lang, targetLangs: Array.isArray(data.target_langs) ? data.target_langs : JSON.parse(data.target_langs || '[]') });
            setTranslatorFee(data.translator_fee || '');
        } catch (err) { setError(err.message); } finally { setLoading(false); }
    }, [jobId, token, t, API_URL]);

    useEffect(() => { fetchJobDetails(); }, [fetchJobDetails]);

    const fetchEligibleTranslators = useCallback(async () => {
        if (!job) return;
        setEligibleTranslators({ loading: true, error: null, data: [] });
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${job.id}/eligible-translators`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(t('error_fetch_translators'));
            const data = await response.json();
            setEligibleTranslators({ loading: false, error: null, data: data });
        } catch (err) { setEligibleTranslators({ loading: false, error: err.message, data: [] }); }
    }, [job, token, t, API_URL]);

    useEffect(() => { if (job) { fetchEligibleTranslators(); } }, [job, fetchEligibleTranslators]);
    
    useEffect(() => {
        const fetchLanguages = async () => {
            if (!token) return;
            try {
                const response = await fetch(`${API_URL}/api/public/languages`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) throw new Error(t('error_fetch_languages'));
                const data = await response.json();
                const formattedLangs = data.map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`, lang.name) }));
                setAvailableLanguages(formattedLangs);
            } catch (error) { console.error("Dil listesi alınamadı:", error); }
        };
        fetchLanguages();
    }, [token, t, API_URL]);

    const handleLanguageChange = async () => {
        if (!languageUpdate.sourceLang || languageUpdate.targetLangs.length === 0) { return alert(t('alert_select_languages')); }
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/languages`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(languageUpdate) });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_language_update'));
            setModalMessage(t(data.messageKey || 'success_languages_updated'));
            setIsSuccessModalOpen(true);
            await fetchJobDetails();
        } catch (err) { alert(err.message); }
    };

    const handleAssignJob = async () => {
        if (!selectedTranslatorId || !translatorFee) { return alert(t('alert_select_translator_and_fee')); }
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ translatorId: selectedTranslatorId, translatorFee }) });
            if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_job_assignment')); }
            const data = await response.json();
            setModalMessage(t(data.messageKey || 'success_job_assigned'));
            setIsSuccessModalOpen(true);
            await fetchJobDetails();
        } catch (err) { alert(err.message); }
    };

    const handleApproveJob = async () => {
        if (!translatorFee || isNaN(translatorFee) || translatorFee <= 0) { return alert(t('alert_enter_valid_fee')); }
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ translatorFee }) });
            if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_job_approval')); }
            const data = await response.json();
            setModalMessage(t(data.messageKey || 'success_job_approved'));
            setIsSuccessModalOpen(true);
            await fetchJobDetails();
        } catch (err) { alert(err.message); }
    };
    
	const handleInvoiceUpload = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('invoiceFile', file);
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/upload-invoice`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(t(data.messageKey) || t('error_invoice_upload'));
            }
			setJob(prevJob => ({
                ...prevJob,
                invoice: data.invoice 
            }));
            
            setModalMessage(t(data.messageKey || 'success_invoice_uploaded'));
            setIsSuccessModalOpen(true);
           
        } catch (err) {
            alert(err.message);
        }
    };
    
	const handleDeleteInvoice = async () => {
        setIsDeleteInvoiceModalOpen(false);
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/invoice`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_invoice_delete'));
            setModalMessage(t(data.messageKey || 'success_invoice_deleted'));
            setIsSuccessModalOpen(true);
            fetchJobDetails();
        } catch (err) {
            alert(err.message);
        }
    };


    const handleTargetFileUpload = async (sourceFileId, fileToUpload) => {
        if (!fileToUpload) return;
        setUploadingState(prev => ({ ...prev, [sourceFileId]: true }));
        const formData = new FormData();
        formData.append('targetFile', fileToUpload);
        formData.append('sourceFileId', sourceFileId);
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/upload-target`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!response.ok) throw new Error(t('error_file_upload'));
           const data = await response.json();
            setModalMessage(t(data.messageKey || 'success_file_uploaded'));
            setIsSuccessModalOpen(true);
            
            await fetchJobDetails(); 
        } catch (err) {
            alert(err.message); 
        } finally {
            setUploadingState(prev => ({ ...prev, [sourceFileId]: false }));
        }
    };
    
    // YENİ: İşe yeni bir kaynak dosya ekler
    const handleAddSourceFile = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('sourceFile', file);
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/add-source-file`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_file_upload'));
            setModalMessage(t('success_file_added', 'Dosya başarıyla eklendi.'));
            setIsSuccessModalOpen(true);
            await fetchJobDetails();
        } catch (err) {
            alert(err.message);
        }
    };
    
    // YENİ: Mevcut bir kaynak dosyayı yenisiyle değiştirir
    const handleChangeSourceFile = async (oldFileId, newFile) => {
        if (!newFile) return;
        const formData = new FormData();
        formData.append('sourceFile', newFile);
        try {
            // "Değiştir" mantığı: Önce eskiyi sil, sonra yeniyi ekle.
            // Bu, kelime sayısının doğru hesaplanmasını sağlar.
            const deleteResponse = await fetch(`${API_URL}/api/support/jobs/${jobId}/source-file/${oldFileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
             if (!deleteResponse.ok) throw new Error(t('error_change_file_partial_delete', 'Dosya değiştirilemedi: Eski dosya silinemedi.'));

            const addResponse = await fetch(`${API_URL}/api/support/jobs/${jobId}/add-source-file`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!addResponse.ok) throw new Error(t('error_change_file_partial_add', 'Eski dosya silindi ama yenisi eklenemedi. Lütfen sayfayı yenileyin.'));

            setModalMessage(t('success_file_changed', 'Dosya başarıyla değiştirildi.'));
            setIsSuccessModalOpen(true);
            await fetchJobDetails();
        } catch (err) {
            alert(err.message);
            await fetchJobDetails(); // Hata durumunda bile en güncel hali getir
        }
    };
    
    // YENİ: Kaynak dosyası silme modalını açar
    const openDeleteSourceFileModal = (fileId) => {
        setSourceFileToDeleteId(fileId);
        setIsDeleteSourceModalOpen(true);
    };

    // YENİ: Kaynak dosyasını silme işlemini onaylar ve API'yi çağırır
    const confirmSourceFileDelete = async () => {
        if (!sourceFileToDeleteId) return;
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/source-file/${sourceFileToDeleteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_file_delete'));
            
            setIsDeleteSourceModalOpen(false);
            setSourceFileToDeleteId(null);
            await fetchJobDetails();
            setModalMessage(t(data.messageKey || 'success_file_deleted'));
            setIsSuccessModalOpen(true);
        } catch (err) {
            alert(err.message);
            setIsDeleteSourceModalOpen(false);
            setSourceFileToDeleteId(null);
        }
    };


 const handleStatusSelectChange = (newStatus) => {
        setStatusToChange(newStatus);
        setIsStatusModalOpen(true); 
    };
    
    
    const confirmStatusChange = async () => {
        if (!statusToChange) return;

        setIsStatusModalOpen(false);
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ newStatus: statusToChange })
            });
            if (!response.ok) throw new Error(t('error_status_update'));
            
            setJob(prev => ({ ...prev, status: statusToChange }));
    
            setModalMessage(t('success_status_updated'));
            setIsSuccessModalOpen(true);

        } catch (err) {
            alert(err.message);
        } finally {
            setStatusToChange(null); 
        }
    };

    const formatDate = (dateString, timezone) => {
        if (!dateString) return '-';
        return formatInTimeZone(new Date(dateString), timezone || 'Europe/Istanbul', 'dd.MM.yyyy HH:mm');
    };

    const JobStatus = ({ status }) => (
        <span className={`status-badge status-${status.toLowerCase()}`}>{t(`job_status_${status.toLowerCase()}`, status)}</span>
    );
  
    const handleEditDeadlineClick = () => {
        if (job && job.translator_deadline) {
            const localDate = new Date(job.translator_deadline);
            localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
            setNewDeadline(localDate.toISOString().slice(0, 16));
        }
        setIsEditingDeadline(true);
    };

    
    const handleUpdateDeadline = async () => {
        if (!newDeadline) return;
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/translator-deadline`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ newDeadline: new Date(newDeadline).toISOString() })
            });
            if (!response.ok) throw new Error(t('error_deadline_update'));
            
            setJob(prev => ({ ...prev, translator_deadline: new Date(newDeadline).toISOString() }));
            setIsEditingDeadline(false);
            alert(t('success_deadline_updated'));
        } catch (err) {
            alert(err.message);
        }
    };
    if (loading) return <div className="page-container"><i className="fas fa-spinner fa-spin"></i> {t('loading_job_details')}</div>;
    if (error) return <div className="page-container error-container"><h3>{t('error_occurred')}</h3><p>{error}</p><Link to="/support/all-jobs" state={{ activeTab: fromTab }} className="back-button"><i className="fas fa-arrow-left"></i> {t('back_to_all_jobs')}</Link></div>;
    if (!job) return <div className="page-container">{t('job_not_found')}</div>;

    const sourceFiles = job.files ? job.files.filter(f => f.file_type === 'source') : [];
    const targetFiles = job.files ? job.files.filter(f => f.file_type === 'target') : [];
    let targetLangsArray = [];
    try {
        if (job.target_langs && typeof job.target_langs === 'string') {
            targetLangsArray = JSON.parse(job.target_langs);
        }
    } catch (e) { console.error("target_langs parse edilemedi:", job.target_langs, e); }
  const handleSendCompletionEmail = async () => {
        setIsConfirmEmailOpen(false); 
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/notify-completion`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setModalMessage(t(data.messageKey || 'success_email_sent'));
            setIsSuccessModalOpen(true);
        } catch (err) {
            alert(err.message);
        }
    };
 
   const openDeleteTargetFileModal = (fileId) => {
        setFileToDeleteId(fileId);
        setIsDeleteModalOpen(true);
    };

   
    const confirmTargetFileDelete = async () => {
        if (!fileToDeleteId) return;
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/target-file/${fileToDeleteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_file_delete'));
            
            setIsDeleteModalOpen(false);
            setFileToDeleteId(null);
            await fetchJobDetails(); 
            setModalMessage(t(data.messageKey || 'success_file_deleted'));
            setIsSuccessModalOpen(true);
        } catch (err) {
            alert(err.message);
            setIsDeleteModalOpen(false);
            setFileToDeleteId(null);
        }
    };
    return (
        <>
           <div className="page-container support-job-detail narrow-content">
            {/* YENİ: Yeni kaynak dosya yüklemek için gizli input */}
            <input 
                type="file" 
                ref={addSourceFileInputRef} 
                style={{display: 'none'}} 
                onChange={(e) => handleAddSourceFile(e.target.files[0])} 
            />
            <header className="detail-page-header support-header">
                <Link to="/management/all-jobs" state={{ activeTab: fromTab }} className="back-button">
                    <i className="fas fa-arrow-left"></i> {t('back_to_all_jobs')}
                </Link>

                <h1>{t('job_details_for_id', { id: job.id })}</h1>
                
                <div className="header-actions-detail">
                    <JobStatus status={job.status} />
                    <select 
                        value={job.status} 
                        onChange={(e) => handleStatusSelectChange(e.target.value)} 
                        className="status-changer"
                    >
                        {jobStatuses.map(s => <option key={s} value={s}>{t(`job_status_${s}`)}</option>)}
                    </select>
                </div>
            </header>
            
            <div className="support-detail-grid">
                <div className="support-detail-main">
                    <div className="detail-card">
                        <h4>{t('order_details')}</h4>
                        <div className="info-grid">
                            <div className="info-item"><span>{t('word_count')}</span><strong>{job.word_count}</strong></div>
                            <div className="info-item"><span>{t('expertise_area')}</span><strong>{t(job.expertise)}</strong></div>
                            <div className="info-item"><span>{t('translation_languages')}</span><strong>{job.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</strong></div>
                            <div className="info-item"><span>{t('order_class')}</span><strong>{t(job.translator_type)}</strong></div>
                            <div className="info-item"><span>{t('order_date')}</span><strong>{formatDate(job.created_at, job.customer_timezone)}</strong></div>
                            <div className="info-item"><span>{t('delivery_speed')}</span><strong>{t(job.delivery_speed === 'fast' ? 'fast_delivery' : 'standard_delivery')}</strong></div>
                            <div className="info-item"><span>{t('deadline_check')}</span><strong>{formatDate(job.deadline, job.customer_timezone)}</strong></div>
							<div className="info-item">
                        <span>{t('translator_deadline')}</span>
                        {isEditingDeadline ? (
                            <div className="deadline-editor">
                                <input 
                                    type="datetime-local" 
                                    value={newDeadline}
                                    onChange={(e) => setNewDeadline(e.target.value)}
                                />
                                <button onClick={handleUpdateDeadline} className="save-deadline-btn"><i className="fas fa-check"></i></button>
                                <button onClick={() => setIsEditingDeadline(false)} className="cancel-deadline-btn"><i className="fas fa-times"></i></button>
                            </div>
                        ) : (
                            <strong onClick={handleEditDeadlineClick} className="editable-deadline" title={t('click_to_edit', 'Düzenlemek için tıkla')}>
                                {formatDate(job.translator_deadline)}
                            </strong>
                        )}
                    </div>
                </div>
                   
                        <div className="extra-services-details">
                            <h4>{t('extra_services')}</h4>
                            <div className="info-line">
                                <span>
                                    {job.requires_dtp && <span className="service-tag">{t('dtp_service')}</span>}
                                    {job.requires_shipping && <span className="service-tag">{t('shipping_service')}</span>}
                                    {job.requires_notary && <span className="service-tag">{t('notary_service')}</span>}
                                    {!job.requires_dtp && !job.requires_shipping && !job.requires_notary && <span>{t('none')}</span>}
                                </span>
                            </div>
                        </div>
                        </div> 
           <div className="detail-card">
                        <div className="financial-details-inline">
                            <h4>{t('financial_details')}</h4>
                            <div className="info-grid">
                                <div className="info-item"><span>{t('customer_price')}</span><strong>{parseFloat(job.price).toLocaleString(i18n.language, {style:'currency', currency: 'TRY'})}</strong></div>
                                <div className="info-item"><span>{t('translator_fee')}</span><strong>{job.translator_fee ? parseFloat(job.translator_fee).toLocaleString(i18n.language, {style:'currency', currency: 'TRY'}) : '-'}</strong></div>
                            </div>
                        </div>
                    </div>
					 {job.status === 'pending_approval' && (
					<div className="detail-card">
                            <h4>{t('actions_and_management')}</h4>
                            <div className="management-section">
                                <h5>{t('update_languages')}</h5>
                                <div className="language-update-group">
                                    <select className="lang-select" value={languageUpdate.sourceLang} onChange={(e) => setLanguageUpdate(p => ({ ...p, sourceLang: e.target.value }))}>
                                        {availableLanguages.map(lang => (<option key={`src-${lang.value}`} value={lang.value}>{lang.label}</option>))}
                                    </select>
                                <i className="fas fa-arrow-right"></i>
                                    <select className="lang-select" value={languageUpdate.targetLangs[0] || ''} onChange={(e) => setLanguageUpdate(p => ({ ...p, targetLangs: [e.target.value] }))}>
                                        <option value="">{t('select_target_language')}</option>
                                        {availableLanguages.map(lang => (<option key={`tgt-${lang.value}`} value={lang.value}>{lang.label}</option>))}
                                    </select>
                                    <button onClick={handleLanguageChange} className="update-btn">{t('update')}</button>
                                </div>
                            </div>

					<div className="management-section">
                            <h5>{t('assign_directly')}</h5>
                            <div className="assign-group">
                                <select value={selectedTranslatorId} onChange={(e) => setSelectedTranslatorId(e.target.value)} disabled={eligibleTranslators.loading}>
                                    <option value="">{eligibleTranslators.loading ? t('loading_translators') : t('select_translator')}</option>
                                    {eligibleTranslators.data.map(tr => <option key={tr.id} value={tr.id}>{tr.first_name} {tr.last_name}</option>)}
                                </select>
                                <input type="number" step="0.01" value={translatorFee} onChange={(e) => setTranslatorFee(e.target.value)} placeholder={t('translator_fee')} className="fee-input-inline" />
                                <button onClick={handleAssignJob} disabled={!selectedTranslatorId || !translatorFee} className="assign-btn">{t('assign_btn')}</button>
                            </div>
                        </div>

                        <div className="management-section">
                            <h5>{t('approve_and_publish_title')}</h5>
                            <div className="approval-group">
                                <div className="fee-input-group">
                                    <label htmlFor={`fee-${job.id}`}>{t('translator_fee')}</label>
                                    <input type="number" id={`fee-${job.id}`} value={translatorFee} onChange={(e) => setTranslatorFee(e.target.value)} placeholder={t('fee_placeholder')} />
                                    <span>(TRY)</span>
                                </div>
                                <button onClick={handleApproveJob} className="approve-btn" disabled={!translatorFee}><i className="fas fa-check"></i> {t('approve_and_publish_btn')}</button>
                            </div>
                        </div>
                    </div>
					 )}
                    <div className="detail-card">
                        <h4>{t('documents')}</h4>
                        <div className="documents-table">
                            <div className="documents-table-header">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                     <span>{t('source_documents')}</span>
                                     {/* YENİ: Yeni Kaynak Dosya Ekle Butonu */}
                                     <button onClick={() => addSourceFileInputRef.current.click()} className="action-btn" style={{padding: '4px 8px', fontSize: '0.8em'}}>
                                         <i className="fas fa-plus"></i> {t('add_new_file', 'Yeni Ekle')}
                                     </button>
                                </div>
                                <span>{t('translated_documents')}</span>
                            </div>
                             <div className="documents-table-body">
                            {sourceFiles.length > 0 ? (
                                sourceFiles.map(sFile => (
                                    <div className="documents-table-row" key={sFile.id}>
                                        <FileRow 
                                            file={sFile} 
                                            type="source" 
                                            t={t} 
                                            onDelete={openDeleteSourceFileModal}
                                            onChangeSource={handleChangeSourceFile}
                                        />
                                        <FileRow 
                                            file={targetFiles.find(tf => tf.source_file_id === sFile.id)} 
                                            sourceFileId={sFile.id}
                                            type="target" 
                                            t={t} 
                                            onUpload={handleTargetFileUpload} 
                                            isUploading={uploadingState[sFile.id]} 
											onDelete={openDeleteTargetFileModal}
                                        />
                                    </div>
                                ))
                            ) : ( <div className="no-files-message">{t('no_source_files_found')}</div> )}
                               </div>
                            </div>
                        </div>
                    </div>
                <div className="support-detail-side">
                    <div className="detail-card">
                        <h4>{t('customer_info')}</h4>
                        <div className="info-line-stacked"><span>{t('full_name')}</span><strong>{job.customer_first_name} {job.customer_last_name}</strong></div>
                        <div className="info-line-stacked"><span>{t('email_address')}</span><strong>{job.customer_email}</strong></div>
                        <div className="info-line-stacked"><span>{t('phone')}</span><strong>{job.customer_phone_code} {job.customer_phone_number}</strong></div>
                   {job.status === 'completed' && (
                            <button 
                                onClick={() => setIsConfirmEmailOpen(true)} 
                                className="action-btn email-btn"
                            >
                                <i className="fas fa-paper-plane"></i> {t('send_completion_email')}
                            </button>
                        )}
                    </div>
					
                   {job.translator_id && !['pending_approval', 'available'].includes(job.status) && (
                        <div className="detail-card">
                            <h4>{t('translator_info')}</h4>
                            <div className="info-line-stacked"><span>{t('full_name')}</span><strong>{job.translator_first_name} {job.translator_last_name}</strong></div>
                            <div className="info-line-stacked"><span>{t('email_address')}</span><strong>{job.translator_email}</strong></div>
                            <div className="info-line-stacked"><span>{t('phone')}</span><strong>{job.translator_phone_code} {job.translator_phone_number}</strong></div>
                        </div>
                    )}
       
	{!['pending_approval', 'available', 'assigned', 'in_progress', 'in_review'].includes(job.status) && (
                        <div className="detail-card">
                            <h4>{t('invoice_management')}</h4>
                            <input type="file" ref={invoiceFileInputRef} style={{display: 'none'}} onChange={(e) => handleInvoiceUpload(e.target.files[0])} accept=".pdf" />
                            {job.invoice ? (
                                <div className="file-display-box">
                                    <span className="file-name-with-icon success"><i className="fas fa-file-pdf"></i> {job.invoice.original_filename}</span>
                                    <div className="file-actions-group-inline">
                                        <a href={`${API_URL}/${job.invoice.filepath}`} download={job.invoice.original_filename} className="inv-download-icon-btn" title={t('download')}><i className="fas fa-download"></i></a>
                                        <button onClick={() => invoiceFileInputRef.current.click()} className="inv-change-icon-btn" title={t('change')}><i className="fas fa-sync-alt"></i></button>
                                        <button onClick={() => setIsDeleteInvoiceModalOpen(true)} className="inv-delete-icon-btn" title={t('delete')}><i className="fas fa-trash-alt"></i></button>
                                    </div>
                                </div>
                            ) : (
                                <button onClick={() => invoiceFileInputRef.current.click()} className="upload-invoice-btn">{t('upload_invoice_btn')}</button>
                            )}
                        </div>
                    )}
                </div>
            </div>
             </div>
            <ConfirmationModal
                isOpen={isConfirmEmailOpen}
                onClose={() => setIsConfirmEmailOpen(false)}
                onConfirm={handleSendCompletionEmail}
                title={t('send_completion_email')}
                message={t('confirm_send_completion_email')}
                confirmButtonText={t('send', 'Gönder')}
            />
            <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                title={t('success', 'Başarılı!')}
                message={modalMessage}
            />
            <ConfirmationModal
                isOpen={isStatusModalOpen}
                onClose={() => setIsStatusModalOpen(false)}
                onConfirm={confirmStatusChange}
                title={t('confirm_status_change_title')}
                message={t('confirm_status_change_prompt', { status: t(`job_status_${statusToChange}`) })}
            />
			<ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmTargetFileDelete}
                title={t('confirm_delete_file_title')}
                message={t('confirm_delete_file')}
                confirmButtonClass="danger" 
            />
            {/* YENİ: Kaynak Dosya Silme Onay Modalı */}
            <ConfirmationModal
                isOpen={isDeleteSourceModalOpen}
                onClose={() => setIsDeleteSourceModalOpen(false)}
                onConfirm={confirmSourceFileDelete}
                title={t('confirm_delete_source_file_title', 'Kaynak Dosyayı Sil')}
                message={t('confirm_delete_source_file_message', 'Bu kaynak dosyayı kalıcı olarak silmek istediğinizden emin misiniz? İşin toplam kelime sayısı değişebilir.')}
                confirmButtonClass="danger"
            />
            <ConfirmationModal
                isOpen={isDeleteInvoiceModalOpen}
                onClose={() => setIsDeleteInvoiceModalOpen(false)}
                onConfirm={handleDeleteInvoice}
                title={t('confirm_delete_invoice_title', 'Faturayı Sil')}
                message={t('confirm_delete_invoice_message', 'Bu faturayı kalıcı olarak silmek istediğinizden emin misiniz?')}
                confirmButtonClass="danger"
            />
        </>
    );
};

export default SupportJobDetailPage;